import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle, PaperPlaneTilt, Spinner } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Chapter, Reveal } from "@/components/site/Reveal";

const initial = { full_name: "", phone: "", email: "", address: "", message: "" };

export const QuoteForm = () => {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.phone || !form.email) {
            toast.error("Please fill in name, phone, and email.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/quotes", form);
            setSuccess(true);
            setForm(initial);
            toast.success("Quote request sent! We'll be in touch soon.");
        } catch (err) {
            const msg =
                err?.response?.data?.detail?.[0]?.msg ||
                err?.response?.data?.detail ||
                "Something went wrong. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Submission failed.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="quote"
            data-testid="quote-section"
            className="relative mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36"
        >
            <Chapter
                index="08"
                label="Free quote"
                testid="quote-chapter"
                title={
                    <>
                        Tell us about{" "}
                        <span className="italic text-brand-accent">your yard</span>.
                    </>
                }
                copy="Send us a few details and we'll come measure, talk through options, and email a flat quote — usually within 24 hours."
            />

            <Reveal>
                <div className="spotlight grid gap-10 rounded-[2rem] border border-brand-border bg-white p-8 md:grid-cols-12 md:gap-12 md:p-14">
                    <div className="md:col-span-5">
                        <p className="font-display text-2xl font-medium italic leading-snug text-brand-text md:text-3xl">
                            "The best lawn in the neighborhood is the one you
                            never have to mow."
                        </p>

                        <ul className="mt-10 space-y-3">
                            {[
                                "On-site measurement included",
                                "No-pressure consult",
                                "Same-week scheduling typical",
                            ].map((t) => (
                                <li
                                    key={t}
                                    className="flex items-center gap-3 text-sm text-brand-muted"
                                >
                                    <CheckCircle
                                        size={18}
                                        weight="fill"
                                        className="text-brand-accent"
                                    />
                                    {t}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-7">
                        {success ? (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.96 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5 }}
                                className="flex h-full flex-col items-center justify-center rounded-2xl border border-brand-accent/30 bg-brand-accent/5 p-10 text-center"
                                data-testid="quote-success"
                            >
                                <div className="grid h-16 w-16 place-items-center rounded-full bg-brand-accent text-brand-bg">
                                    <CheckCircle size={32} weight="fill" />
                                </div>
                                <h3 className="font-display mt-6 text-3xl font-medium text-brand-text">
                                    Thanks — we got it.
                                </h3>
                                <p className="mt-3 max-w-lg text-lg font-medium leading-relaxed text-brand-muted">
                                    Your request is in our queue. A team member
                                    will reach out within one business day to
                                    schedule a free on-site visit.
                                </p>
                                <button
                                    onClick={() => setSuccess(false)}
                                    className="mt-6 text-sm font-semibold text-brand-accent hover:underline"
                                    data-testid="quote-submit-another"
                                >
                                    Submit another request
                                </button>
                            </motion.div>
                        ) : (
                            <form
                                onSubmit={submit}
                                data-testid="quote-form"
                                className="space-y-4"
                            >
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <Field
                                        id="full_name"
                                        label="Full name"
                                        value={form.full_name}
                                        onChange={update("full_name")}
                                        placeholder="Jordan Hayes"
                                        required
                                    />
                                    <Field
                                        id="phone"
                                        label="Phone"
                                        value={form.phone}
                                        onChange={update("phone")}
                                        placeholder="(208) 555-0144"
                                        required
                                    />
                                </div>
                                <Field
                                    id="email"
                                    label="Email"
                                    type="email"
                                    value={form.email}
                                    onChange={update("email")}
                                    placeholder="you@example.com"
                                    required
                                />
                                <Field
                                    id="address"
                                    label="Project address"
                                    value={form.address}
                                    onChange={update("address")}
                                    placeholder="123 Main St, Boise, ID 83702"
                                />
                                <div>
                                    <label
                                        htmlFor="message"
                                        className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-brand-muted"
                                    >
                                        About your project
                                    </label>
                                    <textarea
                                        id="message"
                                        data-testid="quote-input-message"
                                        rows={5}
                                        value={form.message}
                                        onChange={update("message")}
                                        placeholder="Approximate sq ft, type of install (lawn / putting green / pet area), and any timing notes."
                                        className="w-full rounded-xl border border-brand-border bg-brand-surface/60 px-4 py-3.5 text-sm text-brand-text placeholder:text-brand-muted/50 transition-colors focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent/60"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="quote-submit-button"
                                    className="group mt-2 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-accent px-6 py-4 text-base font-bold text-brand-bg transition-all duration-300 hover:bg-brand-accentHover disabled:cursor-not-allowed disabled:opacity-70"
                                >
                                    {loading ? (
                                        <>
                                            <Spinner
                                                size={18}
                                                weight="bold"
                                                className="animate-spin"
                                            />
                                            Sending…
                                        </>
                                    ) : (
                                        <>
                                            Request free quote
                                            <PaperPlaneTilt
                                                size={18}
                                                weight="bold"
                                                className="transition-transform group-hover:translate-x-0.5"
                                            />
                                        </>
                                    )}
                                </button>

                                <p className="text-center text-xs text-brand-muted">
                                    We respect your privacy. We'll only use these
                                    details to reach you about your quote.
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </Reveal>
        </section>
    );
};

const Field = ({ id, label, type = "text", ...rest }) => (
    <div>
        <label
            htmlFor={id}
            className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-brand-muted"
        >
            {label}
        </label>
        <input
            id={id}
            type={type}
            data-testid={`quote-input-${id.replace(/_/g, "-")}`}
            className="w-full rounded-xl border border-brand-border bg-brand-surface/60 px-4 py-3.5 text-sm text-brand-text placeholder:text-brand-muted/50 transition-colors focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent/60"
            {...rest}
        />
    </div>
);
